import { Flex, ReportSection, ReportSectionItemContent } from '@/components';
import { useBusinessManagers } from '@/features/project/_hooks/business-managers';
import { useProjectHasRelatedOrganizations } from '@/features/project/_hooks/organization';
import type { ProjectModel } from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import { ITEM_SPLITTER, findUpperRecursive } from '@/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type z from 'zod';

type Props = {
  project: z.infer<typeof ProjectModel>;
};

export const IdentitasProgram: FC<Props> = ({ project }) => {
  const { t } = useTranslation();
  const { id } = useId();

  const projectOrganizations = useProjectHasRelatedOrganizations({
    projectId: id,
    params: { showMigrated: true },
    enabled: !!id,
  });

  const nestedOrganizations = useMemo(() => {
    if (!projectOrganizations.data?.length) {
      return undefined;
    }
    return findUpperRecursive(
      project.organizationId,
      projectOrganizations.data,
      [],
    )
      .reverse()
      .map((d) => d.name)
      .join(ITEM_SPLITTER);
  }, [projectOrganizations.data, project.organizationId]);

  // const peopleInChargesData = useFilteredUsers({
  //   params: {
  //     filterBy: 'ids',
  //     ids: project.peopleInChargeIds ?? [],
  //     showMigrated: true,
  //     showDeleted: true,
  //   },
  //   select: (data) =>
  //     data.results.map((person) => {
  //       return [
  //         person.name,
  //         ...(person.organizations?.map((v) => v.name) ?? []),
  //       ]
  //         .filter(Boolean)
  //         .join(ITEM_SPLITTER);
  //     }),
  // });

  const businessManagerNames = useBusinessManagers({
    projectId: id,
    select: (data) => {
      return data.map((person) => {
        return [
          person.name,
          ...(person.organizations?.map((v) => v.name) ?? []),
        ]
          .filter(Boolean)
          .join(ITEM_SPLITTER);
      });
    },
  });

  return (
    <ReportSection
      title={t('project-Identitas-Program')}
      items={[
        {
          indexText: '1.',
          subtitle: t('nama-program'),
          item: (
            <Flex>
              <ReportSectionItemContent value={project.name} />
              <ReportSectionItemContent
                wrapperStyle={{ flex: 2 }}
                label={t('created-fiscal-year')}
                value={project.fiscalYear}
                indexText="4."
                hasLeftBorder
              />
            </Flex>
          ),
        },
        {
          indexText: '2.',
          subtitle: t('project-reference'),
          item: (
            <Flex>
              <ReportSectionItemContent
                value={project.indonesiaData?.acuanProgram}
              />
              <ReportSectionItemContent
                wrapperStyle={{ flex: 2 }}
                label={t('pelaksana-program')}
                value={nestedOrganizations ?? ''}
                indexText="5."
                hasLeftBorder
              />
            </Flex>
          ),
        },
        {
          indexText: '3.',
          subtitle: 'Dasar Hukum',
          item: (
            <Flex>
              <ReportSectionItemContent
                value={project.indonesiaData?.dasarHukumAtauKebijakan}
              />
              <ReportSectionItemContent
                wrapperStyle={{ flex: 2 }}
                label={t('business-manager')}
                value={businessManagerNames.data?.join('\n')}
                indexText="6."
                hasLeftBorder
              />
            </Flex>
          ),
        },
      ]}
    />
  );
};
